import React, { useEffect, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  FormLabel,
  InputAdornment,
  LinearProgress,
  TextField,
  Typography
} from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import PropTypes from 'prop-types'

import { ColorBoxContent } from '../../../common/ui/colorBox'

export default function EditCustomer(props) {
  const {
    formik,
    isAddrSearching,
    deliveryAddrSearchResults,
    setDeliveryAddrSearch,
    setDeliveryAddrSearchResults,
    handleUpdateContact,
    handleUpdateAddress
  } = props

  // Billing props
  const {
    billingAddrSearchResults,
    isBillingAddrSame,
    setBillingAddrSearch,
    setBillingAddrSearchResults,
    setIsBillingAddrSame
  } = props

  const [addressSearch, setAddressSearch] = useState('')
  const [bAddressSearch, setBAddressSearch] = useState('')
  const [addressChanged, setAddressChanged] = useState(false)

  // Checkbox is automatically ticked
  useEffect(() => {
    setIsBillingAddrSame(true)
  }, [])

  useEffect(() => {
    if (formik.values.deliveryAddress.streetName !== '') {
      setAddressSearch(formatAddress(formik.values.deliveryAddress))
    }
  }, [formik.values.deliveryAddress.postalCode])

  const formatAddress = (addressObj) => {
    const formattedBlockNo = addressObj.blockNo ?? ''
    const formattedStreetName = addressObj.streetName ?? ''
    const formattedBuildingName = addressObj.buildingName ?? ''
    const formattedPostalCode = addressObj.postalCode ?? ''
    return `${formattedBlockNo} ${formattedStreetName} ${formattedBuildingName} SINGAPORE ${formattedPostalCode}`
  }

  const handleDeliveryAddrSearchChange = async (e) => {
    setAddressSearch(e.target.value)
    setDeliveryAddrSearch(e.target.value)
    setDeliveryAddrSearchResults([])
  }

  const autoFillDeliveryAddr = (addrDetails) => {
    formik.setFieldValue('deliveryAddress.blockNo', addrDetails.BLK_NO)
    formik.setFieldValue('deliveryAddress.streetName', addrDetails.ROAD_NAME)
    formik.setFieldValue('deliveryAddress.buildingName', addrDetails.BUILDING)
    formik.setFieldValue('deliveryAddress.postalCode', addrDetails.POSTAL)
    setAddressSearch(formatAddress(formik.values.deliveryAddress))
  }

  const autoFillBillingAddr = (addrDetails) => {
    formik.setFieldValue('billingAddress.blockNo', addrDetails.BLK_NO)
    formik.setFieldValue('billingAddress.streetName', addrDetails.ROAD_NAME)
    formik.setFieldValue('billingAddress.buildingName', addrDetails.BUILDING)
    formik.setFieldValue('billingAddress.postalCode', addrDetails.POSTAL)
    setBAddressSearch(formatAddress(formik.values.billingAddress))
  }

  /* Checkbox handler for if billing address is same as delivery address */

  const handleBillingCheckbox = (event) => {
    setIsBillingAddrSame(event.target.checked)
  }

  /* Billing Address Functions */

  const handleBillingAddrSearchChange = (e) => {
    setBAddressSearch(e.target.value)
    setBillingAddrSearch(e.target.value)
    setBillingAddrSearchResults([])
  }

  const handleFieldChange = (e) => {
    formik.handleBlur(e)
    formik.handleChange(e)
    setAddressChanged(true)
  }

  return (
    <>
      <ColorBoxContent style={{ marginBottom: 16, marginTop: 8 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            padding: '0px 12px'
          }}
        >
          <Typography variant='h4' sx={{ marginBottom: '24px' }}>
            Company Info
          </Typography>
          <Box
            sx={{
              display: 'flex',
              width: '100%'
            }}
          >
            <Box sx={{ width: '50%' }}>
              <FormLabel>Company Name</FormLabel>
              <TextField
                variant='standard'
                disabled
                sx={{ width: '95%' }}
                value={formik.values.companyName}
              />
            </Box>
            <Box sx={{ marginBottom: '24px' }}>
              <FormLabel>Unique Entity Number (UEN)</FormLabel>
              <TextField
                id='uen'
                name='uen'
                variant='standard'
                disabled
                sx={{ width: '95%' }}
                value={formik.values.uen}
              />
            </Box>
          </Box>
        </Box>
      </ColorBoxContent>
      <ColorBoxContent style={{ marginBottom: 16 }}>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            padding: '0px 12px'
          }}
        >
          <Typography variant='h4' sx={{ marginBottom: '24px' }}>
            Contact Details
          </Typography>
          <Box sx={{ marginBottom: '24px', width: '100%' }}>
            <Box sx={{ marginBottom: '24px', display: 'flex', width: '100%' }}>
              <Box sx={{ width: '50%' }}>
                <FormLabel sx={{ marginBottom: 0 }}>Contact Name</FormLabel>
                <Box sx={{ margin: '8px 4px' }}>
                  <TextField
                    id='contactName'
                    variant='standard'
                    name='contactName'
                    value={formik.values.contactName}
                    onChange={(e) => {
                      formik.handleBlur(e)
                      formik.handleChange(e)
                    }}
                    sx={{ marginTop: '0px', width: '95%' }}
                    error={
                      formik.touched.contactName &&
                      Boolean(formik.errors.contactName)
                    }
                    helperText={
                      formik.touched.contactName && formik.errors.contactName
                    }
                    data-cy='contactName'
                  />
                </Box>
              </Box>
              <Box width='50%'>
                <FormLabel sx={{ marginBottom: 0 }}>Contact Number</FormLabel>
                <TextField
                  id='contactNumber'
                  variant='standard'
                  name='contactNumber'
                  value={formik.values.contactNumber}
                  onChange={(e) => {
                    formik.handleBlur(e)
                    formik.handleChange(e)
                  }}
                  style={{ marginTop: '0px', width: '95%' }}
                  error={
                    formik.touched.contactNumber &&
                    Boolean(formik.errors.contactNumber)
                  }
                  helperText={
                    formik.touched.contactNumber && formik.errors.contactNumber
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>+65</InputAdornment>
                    )
                  }}
                  data-cy='contactNumber'
                />
              </Box>
            </Box>
            <Box display='flex' width='100%'>
              <Box width='50%'>
                <FormLabel sx={{ marginBottom: 0 }}>Email</FormLabel>

                <TextField
                  id='email'
                  variant='standard'
                  name='email'
                  value={formik.values.email}
                  onChange={(e) => {
                    formik.handleBlur(e)
                    formik.handleChange(e)
                  }}
                  sx={{ marginTop: '0px', width: '95%' }}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  data-cy='contactEmail'
                />
              </Box>
              <Box width='50%' />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row-reverse'
            }}
          >
            <Button
              variant='filled'
              onClick={handleUpdateContact}
              disabled={
                !formik.touched.contactName &&
                !formik.touched.contactNumber &&
                !formik.touched.email
              }
              data-cy='saveChangesContact'
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </ColorBoxContent>
      <ColorBoxContent style={{ marginBottom: 16 }}>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            padding: '0px 12px'
          }}
        >
          <Typography variant='h4' sx={{ marginBottom: '50px' }}>
            Delivery Address
          </Typography>
          <Box sx={{ marginBottom: 4 }}>
            <FormLabel sx={{ marginBottom: 0 }}>Address</FormLabel>
            {isAddrSearching ? <LinearProgress /> : ''}
            <Autocomplete
              id='auto-complete'
              options={deliveryAddrSearchResults.map(
                (option) => option.ADDRESS
              )}
              autoSelect
              clearOnBlur
              disableClearable
              value={addressSearch}
              onChange={(_e, value, reason) => {
                if (reason === 'selectOption') {
                  const address = deliveryAddrSearchResults.find(
                    (element) => element.ADDRESS === value
                  )
                  autoFillDeliveryAddr(address)
                  setAddressChanged(true)
                }
              }}
              renderInput={(params) => {
                return (
                  <>
                    <Box sx={{ margin: '8px 4px' }}>
                      <TextField
                        variant='standard'
                        value={addressSearch}
                        sx={{ marginTop: '0px' }}
                        onChange={handleDeliveryAddrSearchChange}
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          type: 'search'
                        }}
                        data-cy='deliveryAddress'
                      />
                    </Box>
                  </>
                )
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              width: '100%',
              marginBottom: 4
            }}
          >
            <Box width='50%' sx={{ marginRight: '50px' }}>
              <FormLabel sx={{ marginBottom: 0 }}>Unit No.</FormLabel>
              <TextField
                id='deliveryAddress.unitNo'
                name='deliveryAddress.unitNo'
                sx={{ marginTop: '10px', width: '95%' }}
                variant='standard'
                onChange={handleFieldChange}
                value={formik.values.deliveryAddress.unitNo}
                error={
                  formik.touched.deliveryAddress?.unitNo &&
                  Boolean(formik.errors.deliveryAddress?.unitNo)
                }
                helperText={
                  formik.touched.deliveryAddress?.unitNo &&
                  formik.errors.deliveryAddress?.unitNo
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>#</InputAdornment>
                  )
                }}
                data-cy='unitNo'
              />
            </Box>
            <Box width='50%'>
              <FormLabel sx={{ marginBottom: 0 }}>
                Building Name (Optional)
              </FormLabel>
              <TextField
                id='deliveryAddress.buildingName'
                name='deliveryAddress.buildingName'
                variant='standard'
                sx={{ marginTop: '10px', width: '95%' }}
                onChange={handleFieldChange}
                value={formik.values.deliveryAddress.buildingName}
                data-cy='buildingName'
              />
            </Box>
          </Box>
          <Typography variant='h5'>Billing Address</Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 4
            }}
          >
            <Checkbox
              sx={{ color: '#0000FF' }}
              checked={isBillingAddrSame}
              onChange={handleBillingCheckbox}
              data-cy='billingAddress'
            />
            Billing address is the same as delivery address
          </Box>
          {!isBillingAddrSame && (
            <Box>
              <Box sx={{ marginBottom: '16px' }}>
                <FormLabel sx={{ marginBottom: 0 }}>Billing Address</FormLabel>
                {isAddrSearching ? <LinearProgress /> : ''}
                <Autocomplete
                  id='auto-complete'
                  options={billingAddrSearchResults.map(
                    (option) => option.ADDRESS
                  )}
                  autoSelect
                  clearOnBlur
                  disableClearable
                  value={bAddressSearch}
                  onChange={(_e, value, reason) => {
                    if (reason === 'selectOption') {
                      const address = billingAddrSearchResults.find(
                        (element) => element.ADDRESS === value
                      )
                      autoFillBillingAddr(address)
                      setAddressChanged(true)
                    }
                  }}
                  renderInput={(params) => {
                    return (
                      <>
                        <Box sx={{ margin: '8px 4px' }}>
                          <TextField
                            variant='standard'
                            value={bAddressSearch}
                            sx={{ marginTop: '0px' }}
                            onChange={handleBillingAddrSearchChange}
                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              type: 'search'
                            }}
                          />
                        </Box>
                      </>
                    )
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  width: '100%',
                  marginBottom: 2
                }}
              >
                <Box sx={{ marginRight: '50px', width: '50%' }}>
                  <FormLabel sx={{ marginBottom: 0 }}>Unit No</FormLabel>
                  <TextField
                    id='billingAddress.unitNo'
                    name='billingAddress.unitNo'
                    variant='standard'
                    sx={{ marginTop: '10px', width: '95%' }}
                    onChange={handleFieldChange}
                    value={formik.values.billingAddress.unitNo}
                    disabled={isBillingAddrSame}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>#</InputAdornment>
                      )
                    }}
                  />
                </Box>
                <Box width='50%'>
                  <FormLabel sx={{ marginBottom: 0 }}>
                    Building Name (Optional)
                  </FormLabel>
                  <TextField
                    id='billingAddress.buildingName'
                    name='billingAddress.buildingName'
                    value={formik.values.billingAddress.buildingName}
                    variant='standard'
                    onChange={handleFieldChange}
                    sx={{ marginTop: '10px', width: '95%' }}
                    disabled={isBillingAddrSame}
                  />
                </Box>
              </Box>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row-reverse',
              marginRight: '12px'
            }}
          >
            <Button
              variant='filled'
              onClick={handleUpdateAddress}
              disabled={!addressChanged}
              data-cy='saveChangesDelivery'
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </ColorBoxContent>
    </>
  )
}

EditCustomer.propTypes = {
  formik: PropTypes.obj,
  isAddrSearching: PropTypes.bool,
  handleUpdateContact: PropTypes.func,
  handleUpdateAddress: PropTypes.func,
  setDeliveryAddrSearchResults: PropTypes.func,
  setDeliveryAddrSearch: PropTypes.func,
  deliveryAddrSearchResults: PropTypes.array,
  isBillingAddrSame: PropTypes.bool,
  billingAddrSearchResults: PropTypes.array,
  setBillingAddrSearch: PropTypes.func,
  setBillingAddrSearchResults: PropTypes.func,
  setIsBillingAddrSame: PropTypes.func
}
